// AdminShared.js — Fase 12: Sistema de Diseno Compartido para Admin
import styled, { css, keyframes } from 'styled-components';

// ============================================================
// DESIGN TOKENS
// ============================================================

export const ADMIN_COLORS = {
  primary: '#0f355b',
  accent: '#14b6cb',
  danger: '#dc2626',
  success: '#059669',
  whatsapp: '#25D366',
  text: '#374151',
  textLight: '#6b7280',
  textMuted: '#9ca3af',
  border: '#e5e7eb',
  borderLight: '#f3f4f6',
  bg: '#f8fafc',
  bgHover: '#f1f5f9',
  white: '#ffffff',
};

// ============================================================
// LAYOUT
// ============================================================

export const AdminContainer = styled.div``;

export const AdminTopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const AdminTitle = styled.h2`
  margin: 0;
  color: ${ADMIN_COLORS.primary};
  font-size: 1.3rem;
`;

export const AdminCard = styled.div`
  background: ${ADMIN_COLORS.white};
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 1.5rem;
`;

export const AdminInfoText = styled.p`
  color: ${ADMIN_COLORS.textLight};
  font-size: 0.9rem;
  margin: 0;
`;

// ============================================================
// BUTTONS
// ============================================================

export const AdminButton = styled.button`
  background: ${p =>
    p.$danger ? ADMIN_COLORS.danger :
    p.$success ? ADMIN_COLORS.success :
    p.$secondary ? '#64748b' :
    p.$whatsapp ? ADMIN_COLORS.whatsapp :
    p.$ghost ? 'transparent' :
    ADMIN_COLORS.accent};
  color: ${p => p.$ghost ? ADMIN_COLORS.text : ADMIN_COLORS.white};
  border: ${p => p.$ghost ? `1px solid ${ADMIN_COLORS.border}` : 'none'};
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const AdminSmallButton = styled(AdminButton)`
  padding: 0.3rem 0.6rem;
  font-size: 0.78rem;
`;

export const AdminActionBtn = styled.button`
  background: none;
  border: 1px solid ${p => p.$danger ? ADMIN_COLORS.danger : '#d1d5db'};
  color: ${p => p.$danger ? ADMIN_COLORS.danger : ADMIN_COLORS.text};
  padding: 0.3rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  margin-right: 0.5rem;
  transition: all 0.2s;

  &:hover {
    background: ${p => p.$danger ? '#fef2f2' : ADMIN_COLORS.bg};
    border-color: ${p => p.$danger ? ADMIN_COLORS.danger : ADMIN_COLORS.accent};
    color: ${p => p.$danger ? ADMIN_COLORS.danger : ADMIN_COLORS.accent};
  }
`;

// ============================================================
// TABLE
// ============================================================

export const AdminTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${ADMIN_COLORS.white};
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
`;

// Contenedor para tablas anchas: se desplazan dentro de su panel en vez de
// empujar la página entera hacia los lados (en móvil y en paneles estrechos).
export const AdminTableScroll = styled.div`
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 2px;
`;

export const AdminTh = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  background: ${ADMIN_COLORS.bg};
  color: ${ADMIN_COLORS.primary};
  font-weight: 600;
  font-size: 0.82rem;
  border-bottom: 2px solid ${ADMIN_COLORS.border};
  white-space: nowrap;
`;

export const AdminTd = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${ADMIN_COLORS.borderLight};
  font-size: 0.9rem;
  color: ${ADMIN_COLORS.text};
`;

export const AdminTableRow = styled.tr`
  transition: background 0.15s;
  &:hover td { background: ${ADMIN_COLORS.bgHover}; }
`;

// ============================================================
// MODAL
// ============================================================

export const AdminModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

export const AdminModalContent = styled.div`
  background: ${ADMIN_COLORS.white};
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: ${p => p.$extraWide ? '900px' : p.$wide ? '700px' : '500px'};
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15);
`;

export const AdminModalTitle = styled.h3`
  color: ${ADMIN_COLORS.primary};
  margin: 0 0 1.5rem 0;
  font-size: 1.1rem;
`;

export const AdminModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

// ============================================================
// FORMS
// ============================================================

const inputBase = css`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${ADMIN_COLORS.accent};
    box-shadow: 0 0 0 2px rgba(20,182,203,0.15);
  }
`;

export const AdminFormGroup = styled.div`
  margin-bottom: 1rem;
  ${p => p.$full && 'grid-column: 1 / -1;'}
`;

export const AdminLabel = styled.label`
  display: block;
  font-weight: 600;
  color: ${ADMIN_COLORS.text};
  margin-bottom: 0.3rem;
  font-size: 0.85rem;
`;

export const AdminInput = styled.input`
  ${inputBase}
`;

export const AdminTextarea = styled.textarea`
  ${inputBase}
  resize: vertical;
  min-height: 80px;
`;

export const AdminSelect = styled.select`
  ${inputBase}
  background: ${ADMIN_COLORS.white};
`;

export const AdminFormGrid = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1fr 1fr'};
  gap: 1rem;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

// ============================================================
// BADGE
// ============================================================

export const AdminBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${p => p.$bg || '#f0fdfa'};
  color: ${p => p.$color || ADMIN_COLORS.accent};
`;

// ============================================================
// SUB-TABS
// ============================================================

export const AdminSubTabContainer = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1.5rem;
  background: ${ADMIN_COLORS.bgHover};
  border-radius: 8px;
  padding: 4px;
  overflow-x: auto;
`;

export const AdminSubTab = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: ${p => p.$active ? '600' : '400'};
  font-size: 0.85rem;
  color: ${p => p.$active ? '#fff' : '#64748b'};
  background: ${p => p.$active ? (p.$accentColor || ADMIN_COLORS.accent) : 'transparent'};
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    color: ${p => p.$active ? '#fff' : (p.$accentColor || ADMIN_COLORS.accent)};
    background: ${p => p.$active ? (p.$accentColor || ADMIN_COLORS.accent) : '#e2e8f0'};
  }
`;

// ============================================================
// STAT CARDS
// ============================================================

export const AdminStatCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

export const AdminStatCard = styled.div`
  background: ${ADMIN_COLORS.white};
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border-left: 4px solid ${p => p.$color || ADMIN_COLORS.accent};
`;

export const AdminStatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${ADMIN_COLORS.primary};
`;

export const AdminStatLabel = styled.div`
  font-size: 0.8rem;
  color: ${ADMIN_COLORS.textLight};
  margin-top: 0.25rem;
`;

// ============================================================
// STATES
// ============================================================

export const AdminLoading = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${ADMIN_COLORS.textLight};
  font-size: 0.95rem;
`;

export const AdminEmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${ADMIN_COLORS.textMuted};
  font-size: 0.95rem;
`;

// ============================================================
// SKELETON (carga)
// ============================================================

const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position: 600px 0; }
`;

export const AdminSkeletonLine = styled.div`
  height: ${p => p.$h || 16}px;
  width: ${p => p.$w || '100%'};
  border-radius: 6px;
  margin-bottom: ${p => p.$mb || 12}px;
  background: linear-gradient(90deg, #eef2f7 0%, #f8fafc 50%, #eef2f7 100%);
  background-size: 1200px 100%;
  animation: ${shimmer} 1.4s linear infinite;

  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

export const AdminSkeletonCard = styled.div`
  background: ${ADMIN_COLORS.white};
  border: 1px solid ${ADMIN_COLORS.border};
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1rem;
`;

// ============================================================
// TOGGLE
// ============================================================

export const AdminToggle = styled.button`
  background: ${p => p.$active ? '#d1fae5' : '#fee2e2'};
  color: ${p => p.$active ? '#065f46' : '#991b1b'};
  border: none;
  padding: 0.25rem 0.6rem;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s;
  &:hover { opacity: 0.8; }
`;

// ============================================================
// PAGINATION
// ============================================================

export const AdminPagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  font-size: 0.85rem;
  color: ${ADMIN_COLORS.textLight};

  button {
    border: 1px solid #ddd;
    background: ${ADMIN_COLORS.white};
    padding: 0.3rem 0.6rem;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s;

    &:hover:not(:disabled) { background: ${ADMIN_COLORS.bgHover}; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
    &.active {
      background: ${ADMIN_COLORS.primary};
      color: ${ADMIN_COLORS.white};
      border-color: ${ADMIN_COLORS.primary};
    }
  }
`;

// ============================================================
// SEARCH
// ============================================================

export const AdminSearchInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  width: 260px;
  transition: border-color 0.2s;
  &:focus {
    outline: none;
    border-color: ${ADMIN_COLORS.accent};
    box-shadow: 0 0 0 2px rgba(20,182,203,0.15);
  }
`;
